import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { credentialingService } from '../../../services/credentialingService';

const RenewalRemindersPanel = ({ refreshTrigger }) => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadReminders();
  }, [refreshTrigger]);

  const loadReminders = async () => {
    try {
      setLoading(true);
      const data = await credentialingService?.getRenewalReminders();
      setReminders(data);
    } catch (err) {
      setError(err?.message || 'Failed to load renewal reminders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'scheduled':
        return 'bg-warning/10 text-warning';
      case 'sent':
        return 'bg-primary/10 text-primary';
      case 'failed':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms':
        return 'MessageSquare';
      case 'email':
        return 'Mail';
      case 'voice':
        return 'Phone';
      default:
        return 'Bell';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDaysUntilReminder = (scheduledDate) => {
    if (!scheduledDate) return null;
    const today = new Date();
    const reminderDate = new Date(scheduledDate);
    const diffTime = reminderDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const reminderStats = {
    scheduled: reminders?.filter(r => r?.status === 'scheduled')?.length || 0,
    sent: reminders?.filter(r => r?.status === 'sent')?.length || 0,
    delivered: reminders?.filter(r => r?.status === 'delivered')?.length || 0,
    failed: reminders?.filter(r => r?.status === 'failed')?.length || 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
          <p className="text-xs text-warning mb-1">Scheduled</p>
          <p className="text-xl font-bold text-warning">{reminderStats?.scheduled}</p>
        </div>
        <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
          <p className="text-xs text-primary mb-1">Sent</p>
          <p className="text-xl font-bold text-primary">{reminderStats?.sent}</p>
        </div>
        <div className="bg-success/10 rounded-lg p-3 border border-success/20">
          <p className="text-xs text-success mb-1">Delivered</p>
          <p className="text-xl font-bold text-success">{reminderStats?.delivered}</p>
        </div>
        <div className="bg-destructive/10 rounded-lg p-3 border border-destructive/20">
          <p className="text-xs text-destructive mb-1">Failed</p>
          <p className="text-xl font-bold text-destructive">{reminderStats?.failed}</p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Upcoming Reminders</h3>
        {reminders?.map((reminder) => {
          const daysUntil = getDaysUntilReminder(reminder?.scheduledDate);
          return (
            <div key={reminder?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Award" size={16} className="text-primary" />
                    <p className="font-semibold text-foreground text-sm">
                      {reminder?.credential?.credential_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">{reminder?.reminderType}</p>
                  {reminder?.messageContent && (
                    <p className="text-xs text-muted-foreground italic">{reminder?.messageContent}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reminder?.status)}`}
                >
                  {reminder?.status?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name={getChannelIcon(reminder?.notificationChannel)} size={12} />
                  {reminder?.notificationChannel?.toUpperCase()}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  {formatDate(reminder?.scheduledDate)}
                  {daysUntil !== null && (
                    <span className={`ml-1 ${
                      daysUntil < 0 ? 'text-destructive' :
                      daysUntil === 0 ? 'text-warning' :
                      daysUntil <= 7 ? 'text-warning': 'text-muted-foreground'
                    }`}>
                      ({daysUntil < 0 ? `${Math.abs(daysUntil)} days ago` :
                        daysUntil === 0 ? 'Today' :
                        `in ${daysUntil} days`})
                    </span>
                  )}
                </span>
                {reminder?.sentAt && (
                  <span className="flex items-center gap-1">
                    <Icon name="Check" size={12} />
                    Sent {formatDate(reminder?.sentAt)}
                  </span>
                )}
              </div>

              {reminder?.credential?.expiration_date && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Credential expires: {formatDate(reminder?.credential?.expiration_date)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {reminders?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Icon name="Bell" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-base font-medium text-foreground mb-2">No Reminders</p>
          <p className="text-sm text-muted-foreground">Renewal reminders will appear here</p>
        </div>
      )}
    </div>
  );
};

export default RenewalRemindersPanel;